/**
 * @description Central permission code mappings for the entire application
 *
 * Key features:
 * - Single source of truth for all permission codes
 * - Prevents typos via autocomplete
 * - Enables easy auditing of permission usage
 *
 * @dependencies
 * - backend system_permissions.code (must match exactly, same PR as the SQL seed)
 *
 * @usage
 * import { PERMISSIONS } from '../constants/permissionMappings';
 * <PermissionGate permission={PERMISSIONS.invoices.delete}>
 *   <button>Delete Invoice</button>
 * </PermissionGate>
 *
 * @notes
 * - Permission codes follow format: {category}.{action}
 * - Must match backend system_permissions.code values exactly
 * - discounts.* seeded in migration 210 (LYN-247 Wave 1)
 * - cms.view seeded in migration 216 (Website nav — not company.view)
 */

export const PERMISSIONS = {
  // Employees
  employees: {
    view_all: 'employees.view_all',
    view_self: 'employees.view_self',
    create: 'employees.create',
    edit_all: 'employees.edit_all',
    edit_self: 'employees.edit_self',
    delete: 'employees.delete',
  },

  // Invoices
  invoices: {
    view: 'invoices.view',
    create: 'invoices.create',
    edit: 'invoices.edit',
    delete: 'invoices.delete',
  },

  // Estimates
  estimates: {
    view: 'estimates.view',
    create: 'estimates.create',
    edit: 'estimates.edit',
    delete: 'estimates.delete',
    send: 'estimates.send',
    convert: 'estimates.convert',
  },

  // Customers
  customers: {
    view: 'customers.view',
    create: 'customers.create',
    edit: 'customers.edit',
    delete: 'customers.delete',
  },

  // Products
  products: {
    view: 'products.view',
    create: 'products.create',
    edit: 'products.edit',
    delete: 'products.delete',
  },

  // Categories
  categories: {
    view: 'categories.view',
    create: 'categories.create',
    edit: 'categories.edit',
    delete: 'categories.delete',
  },

  // Vendors
  vendors: {
    view: 'vendors.view',
    create: 'vendors.create',
    edit: 'vendors.edit',
    delete: 'vendors.delete',
  },

  // Payments
  payments: {
    view: 'payments.view',
    create: 'payments.create',
    edit: 'payments.edit',
    void: 'payments.void',
    refund: 'payments.refund',
    reports: 'payments.reports',
  },

  // Scheduling
  scheduling: {
    view: 'scheduling.view',
    view_team: 'scheduling.view_team',
    view_all: 'scheduling.view_all',
    create: 'scheduling.create',
    edit: 'scheduling.edit',
    delete: 'scheduling.delete',
    assign: 'scheduling.assign',
    invite: 'scheduling.invite',
    manage_assignees: 'scheduling.manage_assignees',
    create_shifts: 'scheduling.create_shifts',
    approve_pto: 'scheduling.approve_pto',
  },

  // Transactions
  transactions: {
    view: 'transactions.view',
    export: 'transactions.export',
  },

  // Reports
  reports: {
    view_basic: 'reports.view_basic',
    view_financial: 'reports.view_financial',
  },

  // Time Clock
  time_clock: {
    view: 'time_clock.view',
    view_team: 'time_clock.view_team',
    view_all: 'time_clock.view_all',
    punch: 'time_clock.punch',
    edit: 'time_clock.edit',
    delete: 'time_clock.delete',
    create_manual: 'time_clock.create_manual',
    export: 'time_clock.export',
    manage_pins: 'time_clock.manage_pins',
    settings: 'time_clock.settings',
  },

  // Credit Memos
  credit_memos: {
    view: 'credit_memos.view',
    create: 'credit_memos.create',
    apply: 'credit_memos.apply',
    void: 'credit_memos.void',
  },

  // Settings
  settings: {
    view: 'settings.view',
    edit: 'settings.edit',
  },

  // Teams (UI not yet built)
  teams: {
    manage: 'teams.manage',
  },

  // PTO (Paid Time Off)
  pto: {
    view: 'pto.view',
    view_all: 'pto.view_all',
    manage: 'pto.manage',
    request: 'pto.request',
  },

  // Locations
  locations: {
    create: 'locations.create',
    edit: 'locations.edit',
    delete: 'locations.delete',
  },

  // Outreach
  outreach: {
    view: 'outreach.view',
    manage: 'outreach.manage',
  },

  // Company (nav visibility + Company Settings / Merchant Alias)
  company: {
    view: 'company.view',
    edit: 'company.edit',
  },

  // Recurring Plans
  recurring: {
    view: 'recurring.view',
    create: 'recurring.create',
    edit: 'recurring.edit',
    cancel: 'recurring.cancel',
  },

  // Discounts (LYN-247 Wave 1 — codes match migration 210)
  discounts: {
    view: 'discounts.view',
    create: 'discounts.create',
    edit: 'discounts.edit',
    delete: 'discounts.delete',
  },

  // Website / CMS (migration 216). Distinct from company.view — Employees
  // already have Company; Website is granted to Admin/Manager by default.
  cms: {
    view: 'cms.view',
  },
};

// Flat list for validation/autocomplete
export const ALL_PERMISSION_CODES = Object.values(PERMISSIONS)
  .flatMap(category => Object.values(category));

export default PERMISSIONS;
